import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink';

import { VipDiscountListByCompanyResponse } from '@app/api/models';
import { VipDiscountsActions } from '@app/vip-discounts/store/vip-discounts.slice';
import {
  cancellationMockDate,
  cancellationMockDateHu,
} from '@shared/constants/cancellation-mock-date';
import { dateToParam } from '@shared/helpers/date-to-param.helper';

import {
  VipDiscountsSellingActions,
  VipDiscountsSellingSelectors,
} from '../../../store/vip-discounts-selling.slice';

@Component({
  selector: 'app-vip-discounts-given-table',
  templateUrl: './vip-discounts-given-table.component.html',
  styleUrls: ['./vip-discounts-given-table.component.scss'],
})
export class VipDiscountsGivenTableComponent implements OnInit, OnDestroy {
  @Input() set discount(value: VipDiscountListByCompanyResponse) {
    this.originalValue = structuredClone(value);
  }
  @Input() set tableIndex(idx: number) {
    this.tableIdx = idx;
    this.isMerchantAllGivenProductsSelected$ = this.store.select(
      VipDiscountsSellingSelectors.isMerchantAllGivenProductsSelected(idx)
    );
  }
  @Input() showCaptionRow = false;

  originalValue!: VipDiscountListByCompanyResponse;

  tableIdx!: number;

  isMerchantAllGivenProductsSelected$!: Observable<any>;
  isAllGivenMerchantsSelected$ = this.store.select(
    VipDiscountsSellingSelectors.isAllGivenMerchantsSelected
  );

  selectedGivenProductsForGroupOperation$ = this.store.select(
    VipDiscountsSellingSelectors.selectSelectedGivenProductsForGroupOperation
  );
  selectedGivenProductsForGroupOperation: Array<string> = [];

  discountModeOptions = [
    { label: 'VIP_DISCOUNTS.PERCENT', value: 'PERCENT' },
    { label: 'VIP_DISCOUNTS.AMOUNT', value: 'AMOUNT' },
    { label: 'VIP_DISCOUNTS.FIX_PRICE', value: 'FIX_PRICE' },
  ];

  formGroup: FormGroup = new FormGroup({
    vipDiscountList: new FormArray([]),
  });

  get formArray(): FormArray {
    return this.formGroup?.get('vipDiscountList') as FormArray;
  }

  editingRowIndexes: Array<number> = [];

  subs = new SubSink();

  today = new Date();
  cancellationMockDateHu = cancellationMockDateHu;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.formArray.clear({ emitEvent: false });
    this.originalValue.vipDiscountList?.forEach((product) => {
      const discountRow = new FormGroup({
        discountId: new FormControl(product.discountId), // hidden
        packagingUnitId: new FormControl(product.packagingUnitId), // hidden
        sortimentId: new FormControl(product.sortimentId), // hidden
        packagingUnitLabel: new FormControl(product.packagingUnitLabel),
        discountValue: new FormControl(product.discountValue, [
          Validators.required,
          Validators.min(0.0001),
        ]),
        discountMode: new FormControl(product.discountMode),
        endDate: new FormControl(product.endDate),
        startDate: new FormControl(product.startDate),
      });
      this.formArray.push(discountRow);
    });

    // remove deleted from table and original value
    this.subs.sink = this.store
      .select(VipDiscountsSellingSelectors.selectDeletedDiscountIndex)
      .subscribe((index) => {
        if (index.tableIndex === this.tableIdx) {
          this.formArray.removeAt(index.rowIndex);
          this.originalValue.vipDiscountList?.slice(index.rowIndex, 1);
        }
      });

    // update original value
    this.subs.sink = this.store
      .select(VipDiscountsSellingSelectors.selectModifiedDiscountIndex)
      .subscribe((index) => {
        if (index.tableIndex === this.tableIdx) {
          this.originalValue.vipDiscountList![index.rowIndex] =
            this.formArray.at(index.rowIndex).value;
        }
      });

    // reset table row value
    this.subs.sink = this.store
      .select(VipDiscountsSellingSelectors.selectModifyFailedDiscountIndex)
      .subscribe((index) => {
        if (index.tableIndex === this.tableIdx) {
          this.formArray
            .at(index.rowIndex)
            .patchValue(this.originalValue.vipDiscountList![index.rowIndex]);
        }
      });

    this.subs.sink = this.selectedGivenProductsForGroupOperation$.subscribe(
      (selection) => {
        this.selectedGivenProductsForGroupOperation = selection;
      }
    );
  }

  editRow(index: number) {
    this.editingRowIndexes.push(index);
    if (this.formArray.at(index).value.endDate === cancellationMockDate) {
      this.formArray.at(index).patchValue({ endDate: '' });
    }
    if (this.formArray.at(index).value.startDate < dateToParam(this.today)) {
      this.formArray.at(index).patchValue({ startDate: this.today });
    }
  }

  cancelRowEdit(index: number) {
    const i = this.editingRowIndexes.indexOf(index);
    this.editingRowIndexes.splice(i, 1);
    this.formArray
      .at(index)
      .patchValue(this.originalValue.vipDiscountList![index]);
  }

  discountModeChanged(rowIndex: number) {
    this.formArray
      .at(rowIndex)
      .get('discountValue')
      ?.setValidators(
        this.formArray.at(rowIndex).get('discountMode')?.value === 'PERCENT'
          ? [Validators.required, Validators.min(0.0001), Validators.max(99)]
          : [Validators.required, Validators.min(0.0001)]
      );
    this.formArray.at(rowIndex).get('discountValue')?.updateValueAndValidity();
  }

  isProductChecked(discountId?: string): boolean {
    if (discountId) {
      return this.selectedGivenProductsForGroupOperation.includes(discountId);
    }
    return false;
  }

  toggle(discountId: string) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectGivenDiscountProduct.trigger({
        discountId: discountId,
      })
    );
  }

  toggleAll(checked: boolean) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectGivenDiscountMerchant.trigger({
        discounts:
          this.originalValue?.vipDiscountList?.map(
            (discount) => discount.discountId ?? ''
          ) ?? [],
        select: checked,
      })
    );
  }

  toggleAllMerchants(checked: boolean) {
    this.store.dispatch(
      VipDiscountsSellingActions.selectGivenDiscountAllMerchants.trigger({
        select: checked,
      })
    );
  }

  saveRow(index: number) {
    const form = this.formArray.at(index);
    if (form.valid) {
      if (!form.value.startDate) {
        form.patchValue({ startDate: new Date() });
      }
      if (!form.value.endDate) {
        form.patchValue({ endDate: new Date(cancellationMockDate) });
      }
      this.store.dispatch(
        VipDiscountsSellingActions.editDiscount.trigger({
          request: {
            vipDiscountId: form.value.discountId,
            body: {
              discountValue: form.value.discountValue,
              discountMode: form.value.discountMode,
              startDate: dateToParam(form.value.startDate),
              endDate: dateToParam(form.value.endDate),
            },
          },
          index: { tableIndex: this.tableIdx, rowIndex: index },
        })
      );
      const i = this.editingRowIndexes.indexOf(index);
      this.editingRowIndexes.splice(i, 1);
    }
  }

  deleteRow(index: number) {
    this.store.dispatch(
      VipDiscountsSellingActions.deleteDiscount.trigger({
        discountId: this.formArray.at(index).value.discountId,
        index: { tableIndex: this.tableIdx, rowIndex: index },
      })
    );
  }

  showOriginalPrice(index: number) {
    this.store.dispatch(
      VipDiscountsActions.loadOriginalPrice.trigger({
        discount: this.formArray.at(index).value,
        isRequestedAsDiscountReceiver: false,
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
